"use client";

import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import type { ReactNode } from "react";
import { QueryProviderWithAuth } from "@/components/providers/query-provider-with-auth";
import { RealtimeUserSyncProvider } from "@/components/providers/realtime-user-sync-provider";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { TRPCReactProvider } from "@/lib/trpc/react";

/**
 * Providers that need to be inside SupabaseProvider
 * This includes QueryProvider with auth state listeners
 */
export function ProvidersWithAuth({ children }: { children: ReactNode }) {
	return (
		<NuqsAdapter>
			<QueryErrorResetBoundary>
				{({ reset }) => (
					<ErrorBoundary
						fallback={({ error, retry }) => (
							<ErrorFallbackWithQueryReset
								error={error}
								retry={retry}
								reset={reset}
							/>
						)}
					>
						<QueryProviderWithAuth>
							<TRPCReactProvider>
								<RealtimeUserSyncProvider>
									<NextThemesProvider
										attribute="class"
										defaultTheme="dark"
										disableTransitionOnChange
										enableColorScheme={false}
									>
										{children}
									</NextThemesProvider>
								</RealtimeUserSyncProvider>
							</TRPCReactProvider>
						</QueryProviderWithAuth>
					</ErrorBoundary>
				)}
			</QueryErrorResetBoundary>
		</NuqsAdapter>
	);
}

function ErrorFallbackWithQueryReset({
	error,
	retry,
	reset,
}: {
	error: Error;
	retry: () => void;
	reset: () => void;
}) {
	const handleRetry = () => {
		reset(); // Reset React Query error state
		retry(); // Reset React error boundary
	};

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="w-full max-w-md rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
						<svg
							className="h-6 w-6 text-destructive"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
							/>
						</svg>
					</div>
					<h3 className="mb-2 font-semibold text-lg">Something went wrong</h3>
					<p className="mb-4 text-muted-foreground text-sm">
						We've been notified about this issue and are working to fix it.
					</p>

					{process.env.NODE_ENV === "development" && (
						<div className="mb-4 rounded-md bg-muted p-3">
							<p className="font-mono text-muted-foreground text-xs">
								{error.message}
							</p>
						</div>
					)}

					<button
						onClick={handleRetry}
						className="inline-flex w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 font-medium text-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					>
						<svg
							className="mr-2 h-4 w-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							/>
						</svg>
						Try again
					</button>
				</div>
			</div>
		</div>
	);
}
